import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BarChart3, Building2, CheckSquare, Filter, Crosshair, TrendingUp, Users, Youtube, Linkedin, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchCrmDashboard, fetchDeals, fetchLeads, fetchTasks } from '../../redux/crmSlice';
import { fetchCampaigns, fetchMarketingAnalytics, fetchMarketingDashboard, fetchSegments } from '../../redux/marketingSlice';
import DashboardModuleShell from '../../components/DashboardModuleShell';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { revopsModules } from '../../config/revopsModules';
import { formatCurrency } from '../../utils/currency';
import { cn } from '../../utils/cn';

function StatBlock({ label, value, hint }) {
  return (
    <div className="rounded-[4px] border border-[#D8DDE6] bg-white p-4">
      <div className="text-[11px] font-bold text-[#706E6B]">{label}</div>
      <div className="text-[24px] font-bold text-[#2E2E2E] tracking-tight mt-2">{value}</div>
      <div className="text-[12px] text-[#706E6B] mt-1">{hint}</div>
    </div>
  );
}

export default function RevenueWorkspacePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { leads, deals, tasks, accounts, contacts, analytics: crmAnalytics, loading: crmLoading } = useSelector((state) => state.crm);
  const { campaigns, segments, dashboard: marketingDashboard, analytics: marketingAnalytics, loading: marketingLoading } = useSelector((state) => state.marketing);

  useEffect(() => {
    dispatch(fetchCrmDashboard());
    dispatch(fetchLeads({ limit: 5 }));
    dispatch(fetchDeals({ limit: 5 }));
    dispatch(fetchTasks({ limit: 5 }));
    dispatch(fetchMarketingDashboard());
    dispatch(fetchMarketingAnalytics());
    dispatch(fetchCampaigns());
    dispatch(fetchSegments());
  }, [dispatch]);

  const stats = useMemo(() => [
    { label: 'CRM Leads', value: leads.length || crmAnalytics.dashboard?.kpis?.totalLeads || 0, icon: Users },
    { label: 'Open Deals', value: deals.length || crmAnalytics.dashboard?.kpis?.openDeals || 0, icon: TrendingUp },
    { label: 'Campaigns', value: campaigns.length, icon: Crosshair },
    { label: 'Segments', value: segments.length, icon: Filter },
  ], [leads.length, deals.length, campaigns.length, segments.length, crmAnalytics.dashboard]);

  const crmCards = [
    { title: 'Leads', to: '/dashboard/crm/leads', icon: Users, value: leads.length || crmAnalytics.dashboard?.kpis?.totalLeads || 0, description: 'List views, lead details, and conversion flow' },
    { title: 'Deals', to: '/dashboard/crm/deals', icon: TrendingUp, value: deals.length || crmAnalytics.dashboard?.kpis?.openDeals || 0, description: 'Pipeline, forecast, and deal detail pages' },
    { title: 'Accounts', to: '/dashboard/crm/accounts', icon: Building2, value: accounts.length, description: 'Company records with linked contacts and deals' },
    { title: 'Tasks', to: '/dashboard/crm/tasks', icon: CheckSquare, value: tasks.length, description: 'Follow-ups and task tracking by owner' },
  ];

  const marketingCards = [
    { title: 'Marketing Overview', to: '/dashboard/crm/marketing', icon: BarChart3, value: marketingDashboard?.totalLeads || 0, description: 'Top-level performance, funnel, and channel metrics' },
    { title: 'Campaigns', to: '/dashboard/crm/marketing/campaigns', icon: Crosshair, value: campaigns.length, description: 'Campaign list view, status tracking, and results' },
    { title: 'Segments', to: '/dashboard/crm/marketing/segments', icon: Filter, value: segments.length, description: 'Audience builder and preview counts' },
    { title: 'Acquisition', to: '/dashboard/crm/marketing/acquisition', icon: TrendingUp, value: marketingDashboard?.newLeads || 0, description: 'Inbound marketing lead lists and source tracking' },
    { title: 'Journeys', to: '/dashboard/crm/marketing/journeys', icon: Zap, value: marketingAnalytics?.openRate || '0%', description: 'Automations and nurture journey management' },
    { title: 'Reports', to: '/dashboard/crm/marketing/reports', icon: BarChart3, value: campaigns.length, description: 'Lead generation reporting and campaign analysis' },
  ];

  const socialCards = [
    { title: 'Ad Configuration', to: '/dashboard/crm/marketing/unified-dashboard', icon: Crosshair, value: campaigns.length, description: 'Single place to connect channels and control ad operations' },
    { title: 'Meta Ads', to: '/dashboard/crm/marketing/meta-manager', icon: Crosshair, value: campaigns.length, description: 'Run Meta ad integrations and campaigns' },
    { title: 'LinkedIn Ads', to: '/dashboard/crm/marketing/linkedin-manager', icon: Linkedin, value: campaigns.length, description: 'Manage LinkedIn ad connectivity and campaign execution' },
    { title: 'YouTube Ads', to: '/dashboard/crm/marketing/youtube-manager', icon: Youtube, value: 'New', description: 'Video ad workspace for YouTube channel integration' },
  ];

  const loading = crmLoading || marketingLoading;

  return (
    <DashboardModuleShell
      title="Revenue Workspace"
      subtitle="Unified CRM and Marketing workspace. CRM is now the single parent area, with marketing nested inside it like one Salesforce-style RevOps app."
      icon={BarChart3}
      stats={stats}
    >
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.7fr)_360px] gap-6">
        <div className="space-y-6">
          <Card>
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[14px] font-bold text-[#2E2E2E]">Social Channels</div>
                <div className="text-[12px] text-[#706E6B]">All platform integrations and ad execution controls in one place.</div>
              </div>
              <Badge variant="amber">Single Channel Hub</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {socialCards.map((item) => (
                <button
                  key={item.title}
                  type="button"
                  onClick={() => navigate(item.to)}
                  className="text-left rounded-[4px] border border-[#D8DDE6] bg-[#FBFCFE] p-4 hover:border-[#0176D3] transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 rounded-[4px] bg-[#0176D3]/10 border border-[#0176D3]/15 flex items-center justify-center text-[#0176D3]">
                      <item.icon size={18} />
                    </div>
                    <div className="text-[20px] font-bold text-[#2E2E2E]">{item.value}</div>
                  </div>
                  <div className="text-[14px] font-bold text-[#2E2E2E]">{item.title}</div>
                  <div className="text-[12px] text-[#706E6B] mt-1">{item.description}</div>
                </button>
              ))}
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[14px] font-bold text-[#2E2E2E]">CRM Workspace</div>
                <div className="text-[12px] text-[#706E6B]">Sales objects and detail views grouped under one parent hub.</div>
              </div>
              <Badge variant="blue">Unified</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {crmCards.map((item) => (
                <button
                  key={item.title}
                  type="button"
                  onClick={() => navigate(item.to)}
                  className="text-left rounded-[4px] border border-[#D8DDE6] bg-[#FBFCFE] p-4 hover:border-[#0176D3] transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 rounded-[4px] bg-[#0176D3]/10 border border-[#0176D3]/15 flex items-center justify-center text-[#0176D3]">
                      <item.icon size={18} />
                    </div>
                    <div className="text-[20px] font-bold text-[#2E2E2E]">{item.value}</div>
                  </div>
                  <div className="text-[14px] font-bold text-[#2E2E2E]">{item.title}</div>
                  <div className="text-[12px] text-[#706E6B] mt-1">{item.description}</div>
                </button>
              ))}
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[14px] font-bold text-[#2E2E2E]">Lead Generation & Analysis</div>
                <div className="text-[12px] text-[#706E6B]">CRM and marketing modules for campaigns, acquisition, segments, and lead analytics.</div>
              </div>
              <Badge variant="emerald">CRM + Marketing</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {marketingCards.map((item) => (
                <button
                  key={item.title}
                  type="button"
                  onClick={() => navigate(item.to)}
                  className="text-left rounded-[4px] border border-[#D8DDE6] bg-[#FBFCFE] p-4 hover:border-[#0176D3] transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 rounded-[4px] bg-[#0176D3]/10 border border-[#0176D3]/15 flex items-center justify-center text-[#0176D3]">
                      <item.icon size={18} />
                    </div>
                    <div className="text-[20px] font-bold text-[#2E2E2E]">{item.value}</div>
                  </div>
                  <div className="text-[14px] font-bold text-[#2E2E2E]">{item.title}</div>
                  <div className="text-[12px] text-[#706E6B] mt-1">{item.description}</div>
                </button>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="space-y-4">
            <div>
              <div className="text-[14px] font-bold text-[#2E2E2E]">Workspace Health</div>
              <div className="text-[12px] text-[#706E6B]">Current high-level object counts across both domains.</div>
            </div>
            <StatBlock label="Total Contacts" value={contacts.length} hint="CRM contact records available for list and detail views" />
            <StatBlock label="Pipeline Value" value={formatCurrency(crmAnalytics.dashboard?.openDealValue || 0, crmAnalytics.dashboard?.baseCurrency || 'INR', { compact: true })} hint="Current deal value from CRM analytics" />
            <StatBlock label="Marketing Conversion" value={`${marketingAnalytics?.conversionRate || 0}%`} hint="Topline marketing conversion from analytics" />
          </Card>

          <Card className="space-y-4">
            <div>
              <div className="text-[14px] font-bold text-[#2E2E2E]">Consolidation Notes</div>
              <div className="text-[12px] text-[#706E6B]">What changed in this pass.</div>
            </div>
            <div className="text-[12px] text-[#706E6B] space-y-2">
              <div>CRM is now the single parent route for Revenue Ops.</div>
              <div>Marketing routes move under `/dashboard/crm/marketing/*`.</div>
              <div>Old `/dashboard/marketing/*` paths are redirected for compatibility.</div>
              <div>Sidebar navigation now points to the unified structure.</div>
            </div>
            <Button variant="outline" loading={loading} onClick={() => window.location.reload()}>Refresh Workspace</Button>
          </Card>

          <Card className="space-y-4">
            <div>
              <div className="text-[14px] font-bold text-[#2E2E2E]">Module Map</div>
              <div className="text-[12px] text-[#706E6B]">All current CRM and marketing modules in the unified tree.</div>
            </div>
            <div className="space-y-2">
              {revopsModules.map((module) => (
                <button
                  key={module.label}
                  type="button"
                  onClick={() => {
                    if (module.comingSoon) {
                      import('../../utils/alert').then(m => m.default.toast('info', 'Coming Soon - Under Development'));
                      return;
                    }
                    navigate(module.to);
                  }}
                  className={cn(
                    "w-full text-left rounded-[4px] border border-[#D8DDE6] bg-white px-3 py-2 hover:border-[#0176D3] transition-colors",
                    module.comingSoon && "opacity-70 grayscale hover:grayscale-0"
                  )}
                >
                  <div className="text-[12px] font-bold text-[#2E2E2E] flex items-center gap-2">
                    {module.label}
                    {module.comingSoon && (
                      <span className="text-[9px] font-bold uppercase tracking-wide text-[#706E6B] bg-[#F3F2F2] border border-[#D8DDE6] rounded-full px-2 py-0.5">Soon</span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#706E6B]">{module.to}</div>
                </button>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </DashboardModuleShell>
  );
}
